import { useAccount } from 'wagmi';

export interface ChainConfig {
  readonly name: string,
  readonly gasCurrencySymbol: string,
  readonly gasCurrencyWrappedAddress: `0x${string}`,
  readonly gasCurrencyDisplayDecimals: number,
  readonly blockExplorer: string,
  readonly rpcUrl: string,
  readonly hoaAddress: `0x${string}`,
  readonly whoaAddress?: `0x${string}`,
  readonly royaltyPoolAddress: `0x${string}`,
  readonly hoamiAddress: `0x${string}`,
  readonly piipAddress: `0x${string}`,
};

interface Config {
  readonly defaultChainId: 369,
  readonly supportedChainIds: Array<number>;
  readonly chainConfigs: {
    [key: number]: ChainConfig
  }
};

export const config: Config = {
  defaultChainId: 369,
  supportedChainIds: [
    369, // PulseChain Mainnet
    943, // PulseChain Testnet v4
    8453, // Base Mainnet
    84532, // Base Sepolia Testnet
  ],

  chainConfigs: {
    369: {
      name: 'PulseChain',
      gasCurrencySymbol: 'PLS',
      gasCurrencyWrappedAddress: '0xA1077a294dDE1B09bB078844df40758a5D0f9a27',
      gasCurrencyDisplayDecimals: 2,
      blockExplorer: 'https://scan.mypinata.cloud/ipfs/bafybeih3olry3is4e4lzm7rus5l3h6zrphcal5a7ayfkhzm5oivjro2cp4/#',
      rpcUrl: 'https://rpc.pulsechain.com',
      hoaAddress: '0x7901a3569679AEc3501dbeC59399F327854a70fe',
      royaltyPoolAddress: "0x3Ee31E984F0aC95b6d9548829e01eFCA970f99Dc",
      hoamiAddress: "0x31246B89f47232f4ee6d094fC9dc44093f28A18e",
      piipAddress: "0x962A4859B3195D5218f9D5cC85adF17a3DC66593",
    },
    943: {
      name: 'PulseChain Testnet v4',
      gasCurrencySymbol: 'tPLS',
      gasCurrencyWrappedAddress: '0x70499adEBB11Efd915E3b69E700c331778628707',
      gasCurrencyDisplayDecimals: 2,
      rpcUrl: 'https://rpc.v4.testnet.pulsechain.com',
      blockExplorer: 'https://scan.v4.testnet.pulsechain.com',
      hoaAddress: '0xbc7A37f00212A1587C00ac9C01Da11BF715fBa2b',
      royaltyPoolAddress: "0x9ef91356b53003C252D69f69B99a530180B199f9",
      hoamiAddress: "0xC7ae582598C720A44F9D8a860061291F2f609132",
      piipAddress: "0xD7BD642C51D0E21942aE60998b848a88742bD590",
    },
    8453: {
      name: 'Base Chain',
      gasCurrencySymbol: 'bETH',
      gasCurrencyWrappedAddress: '0x4200000000000000000000000000000000000006',
      gasCurrencyDisplayDecimals: 2,
      rpcUrl: 'https://mainnet.base.org',
      blockExplorer: 'https://basescan.org/',
      hoaAddress: '0x84B0b1EE6eef971105442Eb9Ab420F3DbB774b46',
      whoaAddress: '0xee642384091f4bb9ab457b875E4e209b5a0BD147',
      royaltyPoolAddress: "0x3Ee31E984F0aC95b6d9548829e01eFCA970f99Dc",
      hoamiAddress: "0x31246B89f47232f4ee6d094fC9dc44093f28A18e",
      piipAddress: "0x962A4859B3195D5218f9D5cC85adF17a3DC66593",
    },
    84532: {
      name: 'Base Sepolia Testnet',
      gasCurrencySymbol: 'bsETH',
      gasCurrencyWrappedAddress: '0x4200000000000000000000000000000000000006',
      gasCurrencyDisplayDecimals: 2,
      rpcUrl: 'https://sepolia.base.org',
      blockExplorer: 'https://sepolia.basescan.org/',
      hoaAddress: '0xAB1080eD1b2AC6b4AEE2a9Fef17622A6e9E1789B',
      whoaAddress: '0xc8b872154F0cEaDEe274A24DBcDC945089b67E96',
      royaltyPoolAddress: "0x79cD02C85873790EE1DD1cBd3B0767fB30c7211D",
      hoamiAddress: "0xB9da3E2D62Bf6e45eC7c7a3cC08Eba7554979021",
      piipAddress: "0x5d8B71195443596CfE438aFeDE3127f5C72a1981",
    },
  },
};

const chainConfigMissingErrorShowed = new Map<number, Date>();

export function useChainConfig() {
  const { chain } = useAccount();

  const chainId = chain?.id ?? config.defaultChainId;
  let chainConfig = config.chainConfigs[chainId];
  if (chainConfig === undefined) {
    if (!chainConfigMissingErrorShowed.has(chainId)) {
      console.error(`No configuration for chain ${chainId}`);
      chainConfigMissingErrorShowed.set(chainId, new Date());
    }
  }
  return {
    chainConfig,
    chain,
    chainId,
  };
}
